type SubmitterElement = HTMLButtonElement | HTMLInputElement;

type ConfirmProceed = () => void;
type ConfirmCancel = () => void;

type ConfirmEventDetail = {
    form: HTMLFormElement;
    message: string;
    submitter: SubmitterElement | null;
    triggeringEvent: SubmitEvent;
    proceed: ConfirmProceed;
    cancel: ConfirmCancel;
};

type ConfirmRuntimeOptions = {
    root?: Document;
};

const DEFAULT_CONFIRM_MESSAGE = 'Are you sure?';

let removeSubmitListener: (() => void) | null = null;
const bypassedForms = new WeakSet<HTMLFormElement>();

const isSubmitterElement = (value: unknown): value is SubmitterElement => (
    value instanceof HTMLButtonElement || value instanceof HTMLInputElement
);

const confirmMessageFor = ({form, submitter}: {
    form: HTMLFormElement;
    submitter: SubmitterElement | null;
}): string | null => {
    const submitterMessage = submitter?.dataset.uiConfirm;
    if (submitterMessage !== undefined) return submitterMessage || DEFAULT_CONFIRM_MESSAGE;

    const formMessage = form.dataset.uiConfirm;
    if (formMessage !== undefined) return formMessage || DEFAULT_CONFIRM_MESSAGE;

    return null;
};

const queueConfirmedSubmit = ({form, submitter}: {
    form: HTMLFormElement;
    submitter: SubmitterElement | null;
}): void => {
    // Chromium can retain the form's submission guard through the current microtask checkpoint.
    window.setTimeout(() => {
        if (!form.isConnected || (submitter && submitter.form !== form)) return;

        bypassedForms.add(form);
        try {
            form.requestSubmit(submitter ?? undefined);
        } finally {
            bypassedForms.delete(form);
        }
    }, 0);
};

const handleSubmit = (event: SubmitEvent): void => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (bypassedForms.has(form)) {
        bypassedForms.delete(form);
        return;
    }

    const submitter = isSubmitterElement(event.submitter) ? event.submitter : null;
    const message = confirmMessageFor({form, submitter});
    if (message === null) return;

    event.preventDefault();

    let settled = false;
    const proceed = (): void => {
        if (settled) return;
        settled = true;
        queueConfirmedSubmit({form, submitter});
    };
    const cancel = (): void => {
        settled = true;
    };
    const confirmEvent = new CustomEvent<ConfirmEventDetail>('ui:confirm', {
        bubbles: true,
        cancelable: true,
        detail: {
            form,
            message,
            submitter,
            triggeringEvent: event,
            proceed,
            cancel,
        },
    });

    form.dispatchEvent(confirmEvent);
    if (confirmEvent.defaultPrevented) return;

    if (window.confirm(message)) {
        proceed();
    } else {
        cancel();
    }
};

const registerConfirm = ({root = document}: ConfirmRuntimeOptions = {}): void => {
    if (removeSubmitListener) return;

    root.addEventListener('submit', handleSubmit as EventListener, {capture: true});
    removeSubmitListener = (): void => root.removeEventListener('submit', handleSubmit as EventListener, {capture: true});
};

const unregisterConfirm = (): void => {
    removeSubmitListener?.();
    removeSubmitListener = null;
};

export {registerConfirm, unregisterConfirm};
export type {ConfirmCancel, ConfirmEventDetail, ConfirmProceed, ConfirmRuntimeOptions};
